import React, { ReactElement, useEffect, useLayoutEffect, useRef, useState } from 'react'

import { dynamicClassName } from 'styles/helper'

import styles from './ResizeableColumn.module.less'

type ResizeableColumnProps = {
  width?: number | string
  minWidth?: number
  collapsed?: boolean
  children?: ReactElement[] | ReactElement
}

export default function ResizeableColumn({
  children,
  width: defaultWidth,
  minWidth = 50,
  collapsed = false,
}: ResizeableColumnProps): ReactElement {
  const [width, setWidth] = useState(defaultWidth ?? `100%`)
  const [isResizing, setIsResizing] = useState(false)
  const container = useRef<HTMLDivElement | null>(null)

  useLayoutEffect(() => {
    setWidth(defaultWidth ?? `100%`)
  }, [defaultWidth])

  useEffect(() => {
    function mouseMove(ev: MouseEvent): void {
      if (!container?.current || !isResizing) {
        return
      }

      const x = ev.clientX
      const containerRect = container.current.getBoundingClientRect()
      const computedWidth = x - containerRect.x + 5

      setWidth(Math.max(minWidth, computedWidth))
    }

    function mouseUp(): void {
      setIsResizing(false)
    }

    if (isResizing) {
      document.addEventListener('mousemove', mouseMove)
      document.addEventListener('mouseup', mouseUp)
    }

    return () => {
      document.removeEventListener('mousemove', mouseMove)
      document.removeEventListener('mouseup', mouseUp)
    }
  }, [isResizing, minWidth])

  return (
    <div
      className={dynamicClassName({
        [styles.ResizeableColumn]: true,
        [styles.ResizeableColumn__active]: isResizing,
        [styles.ResizeableColumn__collapsed]: collapsed,
      })}
      style={{ width }}
      ref={container}
    >
      <div className={styles.ResizeableColumn_Body}>{children}</div>

      <div
        className={dynamicClassName({
          [styles.ResizeableColumn_DragBorder]: true,
          [styles.ResizeableColumn_DragBorder__active]: isResizing,
        })}
        onMouseDown={() => setIsResizing(true)}
      />
    </div>
  )
}
